/**
 * The admin's one status chip (M23-T03).
 *
 * Lifted out of `AppointmentsPage.tsx`, which is where it lived, and given the
 * single tone map the ticket asks for. Five pages carried their own map and
 * **they did not agree**: four read `new` as "waiting on us" (amber) while
 * `EnquiriesPage` read it as "in flight" (accent) and `responded` as amber.
 * The same word rendered as two different states depending on which queue you
 * were looking at.
 *
 * Tones, not colours, so a page names the meaning and never the paint:
 *
 * | tone | means | example statuses |
 * |---|---|---|
 * | `attention` | waiting on us | new, requested, pending, pending_payment |
 * | `active` | in flight | in_progress, confirmed, held, offered |
 * | `done` | resolved, well | completed, responded, converted, released |
 * | `muted` | over, no action | closed, cancelled, declined, expired |
 * | `bad` | failed | failed, error |
 */

import { TONE_STYLES, toneFor, type StatusTone } from '../statusTones'

export type { StatusTone }

export function StatusPill({
  status,
  tone,
  label,
}: {
  status: string
  /** Override the table — only when a page genuinely means something else. */
  tone?: StatusTone
  /** Override the displayed text; defaults to the status with `_` as spaces. */
  label?: string
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
        TONE_STYLES[tone ?? toneFor(status)]
      }`}
    >
      {label ?? status.replace(/_/g, ' ')}
    </span>
  )
}
